/**
 * Chave de semana ISO em UTC — formato "AAAA_Wnn".
 *
 * Espelho de `getISOWeek` / `previousISOWeek` em
 * `functions/src/leagues/leagueTx.ts`. Os dois lados PRECISAM ficar
 * sincronizados: é essa chave que decide se um doc semanal (sala de liga,
 * `global_rankings`, vitrine da loja) é da semana corrente ou lixo velho.
 *
 * ⚠️ NÃO confundir com `isoWeekKey` de `functions/src/shared/time.ts`, que usa
 * hífen ("AAAA-Wnn"). Formatos diferentes por histórico — as salas de liga já
 * existem em produção com underscore.
 */
export function getISOWeekKey(now: Date = new Date()): string {
  const date = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  const dayNum = date.getUTCDay() || 7; // Segunda=1 … Domingo=7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum); // quinta-feira da semana ISO
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    (((date.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7,
  );
  return `${date.getUTCFullYear()}_W${String(weekNo).padStart(2, "0")}`;
}

/**
 * Semana ISO anterior à de `now` — a que o `closeWeeklyLeagues` acabou de
 * fechar quando roda na segunda 00:05 UTC. É a semana que os docs "da última
 * semana fechada" (ex.: `global_rankings/latest`) devem carregar.
 */
export function previousISOWeekKey(now: Date = new Date()): string {
  return getISOWeekKey(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
}

/** Quando o `closeWeeklyLeagues` roda: segunda-feira, 00:05 UTC. */
const LEAGUE_TURN_WEEKDAY = 1; // segunda, na contagem ISO (Domingo=7)
const LEAGUE_TURN_MINUTES_UTC = 5;

/**
 * Quantos dias inteiros faltam para a próxima virada de semana da liga.
 *
 * Existe para o painel de Temas AVISAR antes de uma mudança de preço, e o
 * motivo é medido, não zelo: posse é retroativa (`unlockedThemes` é
 * `arrayUnion`), então ninguém perde tema quando o preço muda — **quem a
 * mudança alcança é quem estava JUNTANDO**. Alguém com 250 guardados encontra
 * 700, e essa é a sensação de "sumiram minhas moedas" registrada em 31/07. O
 * pedido que saiu do repreçamento de 05/09 foi textualmente "aviso ANTES da
 * virada de semana".
 *
 * Devolve 0 no próprio dia da virada (antes das 00:05 já passou o instante, e
 * "hoje" é a resposta honesta para quem está prestes a mexer no preço).
 */
export function daysUntilLeagueTurn(now: Date = new Date()): number {
  const diaISO = now.getUTCDay() || 7;
  let dias = (LEAGUE_TURN_WEEKDAY - diaISO + 7) % 7;
  // Já é segunda: a virada desta semana ou já aconteceu (passou das 00:05) ou
  // acontece em minutos. Nos dois casos o próximo instante útil é daqui a 7
  // dias, e dizer "0" seria prometer uma janela que não existe.
  if (dias === 0) {
    const minutosHoje = now.getUTCHours() * 60 + now.getUTCMinutes();
    if (minutosHoje >= LEAGUE_TURN_MINUTES_UTC) dias = 7;
  }
  return dias;
}
